import ShareMenu from '@/components/social/ShareMenu';

interface CommunityPost {
  id: string;
  club_id: string;
  body: string;
  post_type: string;
  media_urls: { url: string; type: string; alt?: string }[];
  shared_at: string;
  author_name: string;
  author_avatar: string | null;
  channel_name: string | null;
}

interface CommunityPostCardProps {
  post: CommunityPost;
}

const CLUB_LABELS: Record<string, { name: string; color: string }> = {
  'sponsors-club': { name: 'Sponsors Club', color: '#C4A052' },
  'teachers-corner': { name: 'Teachers Corner', color: '#B85C38' },
  'the-combine': { name: 'The Combine', color: '#0EA5E9' },
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export type { CommunityPost };

export default function CommunityPostCard({ post }: CommunityPostCardProps) {
  const club = CLUB_LABELS[post.club_id] || { name: 'Community', color: '#888' };

  return (
    <article className="rounded-2xl border border-sand bg-white p-4 transition-shadow hover:shadow-soft">
      {/* Club badge */}
      <div
        className="mb-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white"
        style={{ backgroundColor: club.color }}
      >
        {club.name}
      </div>

      {/* Author */}
      <div className="flex items-center gap-2.5">
        {post.author_avatar ? (
          <img src={post.author_avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: club.color }}
          >
            {(post.author_name[0] || '?').toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-cocoa truncate">{post.author_name}</p>
          <p className="text-[11px] text-cocoa/50">
            {post.channel_name && <>{post.channel_name} &middot; </>}
            {timeAgo(post.shared_at)}
          </p>
        </div>
      </div>

      {/* Body */}
      {post.body && (
        <p className="mt-2 text-sm text-cocoa/80 leading-relaxed line-clamp-4">
          {post.body}
        </p>
      )}

      {/* Media preview */}
      {post.media_urls.length > 0 && (
        <div className="mt-2">
          <img
            src={post.media_urls[0].url}
            alt={post.media_urls[0].alt || ''}
            className="h-36 w-full rounded-xl object-cover"
          />
        </div>
      )}

      {/* External share */}
      <div className="mt-2 flex justify-end">
        <ShareMenu text={post.body || ''} variant="light" />
      </div>
    </article>
  );
}
